import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';

/**
 * BaseWidget - Wrapper component providing common functionality for all widgets
 * Handles loading states, error states, and remove functionality
 */
const BaseWidget = ({ 
  widgetId, 
  title, 
  children, 
  isLoading = false, 
  error = null, 
  onRemove, 
  isEditMode = false,
  className = "",
  ...props 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const removeButtonRef = useRef(null);
  const contentRef = useRef(null);
  const scrollElementRef = useRef(null);

  const handleRemove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();

    // Directly remove without confirmation
    if (onRemove) {
      onRemove(widgetId);
    }
  };

  // Check if content is scrollable and show scroll indicator
  useEffect(() => {
    let scrollTimeoutId = null;
    let resizeTimeoutId = null;
    
    const checkScrollable = () => {
      if (!contentRef.current) return;
      
      // Find scrollable elements within the content (check for elements with overflow-y: auto or scroll)
      const findScrollableElement = (element) => {
        if (!element) return null;
        
        const style = window.getComputedStyle(element);
        const hasOverflow = style.overflowY === 'auto' || style.overflowY === 'scroll';
        const isScrollable = element.scrollHeight > element.clientHeight;
        
        if (hasOverflow && isScrollable) {
          return element;
        }
        
        // Check children
        for (const child of Array.from(element.children)) {
          const found = findScrollableElement(child);
          if (found) return found;
        }
        
        return null;
      };
      
      // Remove old scroll listener if element changed
      if (scrollElementRef.current) {
        scrollElementRef.current.removeEventListener('scroll', handleScroll);
        scrollElementRef.current = null;
      }
      
      const scrollElement = findScrollableElement(contentRef.current);
      scrollElementRef.current = scrollElement;
      
      if (scrollElement) {
        const hasScrollableContent = scrollElement.scrollHeight > scrollElement.clientHeight;
        const isScrolledToBottom = scrollElement.scrollHeight - scrollElement.scrollTop <= scrollElement.clientHeight + 5; // 5px threshold
        
        setShowScrollIndicator(hasScrollableContent && !isScrolledToBottom);
        
        // Attach scroll listener to the found scrollable element
        scrollElement.addEventListener('scroll', handleScroll);
      } else {
        setShowScrollIndicator(false);
      }
    };

    const handleScroll = () => {
      // Debounce scroll checks
      if (scrollTimeoutId) {
        clearTimeout(scrollTimeoutId);
      }
      scrollTimeoutId = setTimeout(() => {
        if (scrollElementRef.current) {
          const hasScrollableContent = scrollElementRef.current.scrollHeight > scrollElementRef.current.clientHeight;
          const isScrolledToBottom = scrollElementRef.current.scrollHeight - scrollElementRef.current.scrollTop <= scrollElementRef.current.clientHeight + 5;
          setShowScrollIndicator(hasScrollableContent && !isScrolledToBottom);
        }
      }, 50);
    };

    // Check on mount and when children change
    // Use setTimeout to ensure DOM is updated
    const timeoutId = setTimeout(checkScrollable, 100);
    
    // Also check on resize
    const resizeObserver = new ResizeObserver(() => {
      // Clear any existing timeout before scheduling a new one
      if (resizeTimeoutId) {
        clearTimeout(resizeTimeoutId);
      }
      resizeTimeoutId = setTimeout(checkScrollable, 100);
    });
    
    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }
    
    return () => {
      clearTimeout(timeoutId);
      if (scrollTimeoutId) {
        clearTimeout(scrollTimeoutId);
      }
      if (resizeTimeoutId) {
        clearTimeout(resizeTimeoutId);
      }
      if (scrollElementRef.current) {
        scrollElementRef.current.removeEventListener('scroll', handleScroll);
        scrollElementRef.current = null;
      }
      resizeObserver.disconnect();
    };
  }, [children, isLoading, error]);

  return (
    <div 
      className={
        isEditMode
          ? `hover:border-border rounded-card border border-transparent relative h-full w-full flex flex-col ${className}`
          : `rounded-card relative h-full w-full flex flex-col ${className}`
      }
      style={{ height: '100%', minHeight: '100%', position: 'relative' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-widget-loading={isLoading ? "true" : "false"}
      {...props}
    >
      {/* Remove button - positioned in top-right corner, only show in edit mode on hover */}
      {isEditMode && onRemove && (
        <button
          ref={removeButtonRef}
          onClick={handleRemove}
          data-remove-widget-button="true"
          onPointerDown={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
          onPointerDownCapture={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
          onMouseUp={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          className={`
            widget-remove-button
            absolute top-[8px] right-[8px]
            h-[24px] w-[24px] p-0
            flex items-center justify-center
            rounded-full bg-surface-2
            text-text-2
            transition-all duration-200
            hover:bg-surface-3
            ${isHovered ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
          `}
          style={{
            border: '1px solid hsl(var(--border))'
          }}
          title="Remove widget"
          aria-label="Remove widget"
        >
          <FontAwesomeIcon
            icon={icon({ name: 'xmark', family: 'sharp', style: 'light' })}
            className="text-lg cursor-pointer p-1"
          />
        </button>
      )}
      {/* Widget Content */}
      <div 
        className="h-full w-full flex flex-col"
        style={{ minHeight: 0, height: '100%' }}
      >
        {isLoading && (
          <div className="flex items-center justify-center flex-1">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-text"></div>
              <span className="text-text-2 text-sm">Loading...</span>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center flex-1">
            <div className="text-center">
              <FontAwesomeIcon
                icon={icon({ name: 'exclamation-triangle', family: 'sharp', style: 'light' })}
                className="text-text text-2xl mb-2"
              />
              <p className="text-danger text-sm">Error loading data</p>
              <p className="text-text-2 text-xs mt-1">{error}</p>
            </div>
          </div>
        )}

        {!isLoading && !error && (
          <div 
            ref={contentRef}
            className="flex-1 flex flex-col min-h-0 overflow-hidden relative"
            style={{ position: 'relative' }}
            data-widget-content="true"
          >
            {children}
            {/* Scroll indicator - shows when there's more content below */}
            {showScrollIndicator && (
              <div 
                className="absolute pointer-events-none z-10"
                style={{
                  background: 'linear-gradient(to bottom, transparent 0%, rgba(255, 255, 255, 0.8) 50%, rgba(255, 255, 255, 0.95) 100%)',
                  height: '40px',
                  bottom: '5px', // Inset to avoid overlapping widget border
                  left: '21px', // Account for widget padding (16px) + border (1px) + margin (4px)
                  right: '41px', // Account for widget padding (16px) + border (1px) + resize handle space (24px)
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  paddingBottom: '4px',
                  borderBottomLeftRadius: '12px', // Match inner content radius
                  borderBottomRightRadius: '12px'
                }}
              >
                <FontAwesomeIcon
                  icon={icon({ name: 'chevron-down', family: 'sharp', style: 'light' })}
                  className="text-text-2 text-sm"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

BaseWidget.propTypes = {
  widgetId: PropTypes.string.isRequired,
  title: PropTypes.string,
  children: PropTypes.node,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  onRemove: PropTypes.func,
  isEditMode: PropTypes.bool,
  className: PropTypes.string
};

export default BaseWidget;
